import "./ParticipantCard.css";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Section, Field } from "../ParticipantModal/ParticipantModal";
import { fallbackDeep } from "../../utils/constants";
import { getTextByLang } from "../../utils/formUtils";
import {
  getParticipantDisplayName,
  getParticipantLocation,
  getParticipantVocabularyLabel,
  mapParticipantVocabularyValues,
  getParticipantPurposePreview,
  getParticipantStatusLabel,
} from "../../utils/participants";

const ParticipantCard = ({
  participant,
  countryOptions = [],
  provinceOptions = [],
  classificationOptions = [],
  purposeOptions = [],
}) => {
  const { t, i18n } = useTranslation();
  const [flipped, setFlipped] = useState(false);

  const handleFlip = () => {
    setFlipped((prev) => !prev);
  };

  const name =
    getParticipantDisplayName(participant, i18n.language) || "Unknown Name";

  const idData = participant.identifier?.[0] || {};

  const classification =
    getParticipantVocabularyLabel(
      participant.classification?.[0],
      classificationOptions,
      i18n.language,
    ) || "—";
  const purposePreview = mapParticipantVocabularyValues(
    getParticipantPurposePreview(participant, 2),
    purposeOptions,
    i18n.language,
  );

  const connectorData = participant.connector?.[0] || {};
  const status = connectorData.status || participant.status || "UNKNOWN";
  const translatedStatus = getParticipantStatusLabel(status, t);

  const description =
    getTextByLang(participant.description, i18n.language) || "";
  const shortDescription =
    description.length > 150
      ? `${description.slice(0, 150).trim()}...`
      : description;

  const addr = participant.address || {};
  const country =
    getParticipantVocabularyLabel(
      addr.adminUnit1,
      countryOptions,
      i18n.language,
    ) || "—";
  const province =
    getParticipantVocabularyLabel(
      addr.adminUnit2,
      provinceOptions,
      i18n.language,
    ) || "—";
  const fullAddress = addr.fullAddress || "";
  const postCode = addr.postCode || addr.postalCode || "";
  const postName = addr.postName || "";
  const location = getParticipantLocation(
    participant,
    countryOptions,
    i18n.language,
    provinceOptions,
  );

  const contact = participant.contactPoint || {};
  const email = contact.email || "—";
  const phone = contact.telephone || "—";
  const hours = contact.openingHours || "—";

  return (
    <>
      {flipped && <div className="participant-card-placeholder" />}
      {flipped && (
        <div
          className="card-backdrop"
          onClick={handleFlip}
          onKeyDown={(event) => {
            if (
              event.key === "Escape" ||
              event.key === "Enter" ||
              event.key === " "
            ) {
              event.preventDefault();
              handleFlip();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Close participant card"
        />
      )}
      <div
        className={`participant-card-container ${flipped ? "flipped" : ""}`}
        onClick={handleFlip}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleFlip();
          }
        }}
        role="button"
        tabIndex={0}
        aria-pressed={flipped}
      >
        <div className="participant-card front">
          <div className="card-header">
            <span className="badge-category">{classification}</span>
            <div className={`status-pill ${String(status).toLowerCase()}`}>
              <span className="dot"></span> {translatedStatus}
            </div>
          </div>

          <div className="card-body">
            <h3 className="card-title">{name}</h3>
            <p className="card-location">{location}</p>

            {shortDescription ? (
              <p className="card-description">{shortDescription}</p>
            ) : null}

            <div className="purpose-chip-row">
              {purposePreview.length > 0 ? (
                purposePreview.map((item) => (
                  <span key={item} className="purpose-chip">
                    {item}
                  </span>
                ))
              ) : (
                <span className="purpose-chip muted">—</span>
              )}
            </div>
          </div>

          <div className="card-footer">
            <span className="tap-hint">{t("participant.details")} →</span>
          </div>
        </div>

        <div className="participant-card back">
          <button
            className="close-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleFlip();
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

          <div className="modal-header">
            <span className="badge-category large">{classification}</span>
            <h2 className="modal-title">{name}</h2>
            <p className="modal-subtitle">{location}</p>
          </div>

          <div className="modal-scroll-area">
            {description && (
              <div className="info-block full-width">
                <Section title={t("participant.description")}>
                  <div className="row">
                    <Field value={fallbackDeep(description)} />
                  </div>
                </Section>
              </div>
            )}

            <div className="modal-grid">
              {/* Address */}
              <div className="info-block">
                <Section title={t("participant.address")}>
                  <div className="row">
                    <Field value={fallbackDeep(fullAddress)} />
                  </div>

                  <div className="row">
                    <Field
                      label={t("participant.city")}
                      value={fallbackDeep(postName)}
                    />
                    <Field
                      label={t("participant.postal")}
                      value={fallbackDeep(postCode)}
                    />
                  </div>

                  <div className="row">
                    <Field
                      label={t("participant.adminUnit2")}
                      value={fallbackDeep(province)}
                    />
                    <Field
                      label={t("participant.country")}
                      value={fallbackDeep(country)}
                    />
                  </div>
                </Section>
              </div>

              <div className="info-block">
                <Section title={t("participant.purpose")}>
                  <div className="purpose-list">
                    {Array.isArray(participant.purpose) &&
                    participant.purpose.length > 0 ? (
                      participant.purpose.map((item) => (
                        <span key={item} className="purpose-list-chip">
                          {getParticipantVocabularyLabel(
                            item,
                            purposeOptions,
                            i18n.language,
                          )}
                        </span>
                      ))
                    ) : (
                      <Field value="—" />
                    )}
                  </div>
                </Section>
              </div>

              <div className="info-block">
                <Section title={t("participant.contactPoint")}>
                  <div className="row">
                    <Field
                      label={t("participant.email")}
                      value={fallbackDeep(email)}
                      rawValue={email}
                      link
                    />
                  </div>

                  <div className="row">
                    <Field
                      label={t("participant.phone")}
                      value={fallbackDeep(phone)}
                    />
                  </div>

                  {hours && (
                    <div className="row">
                      <Field
                        label={t("participant.openingHours")}
                        value={fallbackDeep(hours)}
                      />
                    </div>
                  )}
                </Section>
              </div>

              <div className="info-block">
                <Section title={t("participant.metadata")}>
                  <div className="row">
                    <Field
                      label={t("participant.nif")}
                      value={fallbackDeep(idData?.notation)}
                    />
                    <Field
                      label={t("participant.status")}
                      value={fallbackDeep(translatedStatus)}
                    />
                  </div>
                </Section>
              </div>

              {idData?.schemeURI && (
                <div className="info-block">
                  <Section title={t("participant.legalData")}>
                    <div className="row">
                      <Field
                        value={
                          <a
                            href={idData.schemeURI}
                            target="_blank"
                            rel="noreferrer"
                            className="external-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {t("participant.registry")} ↗
                          </a>
                        }
                      />
                    </div>
                  </Section>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ParticipantCard;
